import { FunctionComponent, ReactElement, useCallback, useState } from "react";

import { Button, Typography, styled } from "@mui/material";

import { Upload } from "@mui/icons-material";

import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridValueGetterParams,
} from "@mui/x-data-grid";
import { FilePdf } from "@phosphor-icons/react";
import { formatDistance } from "date-fns";

import { EmptyRecords, StatusChip } from "../../components/common";
import { DataGridFooter } from "../../components/common";
import {
    useGetCustomizedOrders,
    useProcessCustomizedOrders,
} from "../../hooks";
import { useMessage, useToggleCallbacks } from "../../sdk/hooks";
import { IPage } from "../../types";
import {
    CustomizedOrderStatus,
    customizedOrderStatusNames,
} from "../../utils/enums";

import { FileUploadModal } from "./FileUploadModal";

const Root = styled("main")`
    padding: ${({ theme }) => theme.spacing(3)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};
`;

const TitleContainer = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Title = styled(Typography)`
    font-size: 24px;
    font-weight: 500;
`;

const EmptyRecordsContainer = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
    align-items: center;
`;

const StyledDataGrid = styled(DataGrid)`
    .MuiDataGrid-cell:focus {
        outline: none;
    }
    .MuiDataGrid-row:hover {
        cursor: pointer;
    }
`;

const CellTitle = styled(Typography)`
    font-size: 16px;
    color: ${({ theme }) => theme.palette.text.primary};
`;

const StyledCell = styled("div")`
    display: flex;
    flex-direction: column;
    align-items: start;
`;

const ButtonContainer = styled("div")`
    display: flex;
    gap: ${({ theme }) => theme.spacing(1)};
`;

const columns: GridColDef[] = [
    {
        field: "name",
        headerName: "Name",
        flex: 0.4,
        minWidth: 300,
        renderCell: (params: GridRenderCellParams) => (
            <StyledCell>
                <CellTitle>{params.row.orderName}</CellTitle>
            </StyledCell>
        ),
    },
    {
        field: "customText",
        headerName: "Custom Text",
        flex: 0.5,
        minWidth: 300,
        renderCell: (params: GridRenderCellParams) => (
            <StyledCell>
                <CellTitle>{params.row.customText}</CellTitle>
            </StyledCell>
        ),
    },
    {
        field: "product",
        headerName: "Product",
        flex: 0.4,
        minWidth: 300,
        renderCell: (params: GridRenderCellParams) => (
            <StyledCell>
                <CellTitle>{params.row.fullTitle}</CellTitle>
            </StyledCell>
        ),
    },
    {
        field: "status",
        headerName: "Status",
        flex: 0.5,
        minWidth: 140,
        maxWidth: 150,
        renderCell: (params: GridRenderCellParams) => (
            <StatusChip
                label={
                    customizedOrderStatusNames[
                        params.row.status as CustomizedOrderStatus
                    ]
                }
                value={params.row.status}
            />
        ),
    },
    {
        field: "createdAt",
        headerName: "Created",
        flex: 0.3,
        minWidth: 150,
        valueGetter: (params: GridValueGetterParams) =>
            `${formatDistance(params.row.createdAt, new Date())} ago`,
    },
    {
        field: "updatedAt",
        headerName: "Last updated",
        flex: 0.3,
        minWidth: 150,
        valueGetter: (params: GridValueGetterParams) =>
            `${formatDistance(params.row.updatedAt, new Date())} ago`,
    },
];

export const CustomizedOrdersScreen: FunctionComponent = (): ReactElement => {
    const [page, setPage] = useState<IPage>({
        limit: 20,
        cursor: null,
        direction: "after",
    });
    const [fileUploadOpen, setFileUploadOpen] = useState<boolean>(false);
    const [openFileUpload, closeFileUpload] =
        useToggleCallbacks(setFileUploadOpen);
    const message = useMessage();

    const fetchCustomizedOrders = useGetCustomizedOrders(
        [
            "customizedOrders",
            page.limit.toString(),
            page.cursor ?? "",
            page.direction,
        ],
        {
            limit: page.limit,
            cursor: page.cursor,
            direction: page.direction,
        },
    );

    const processCustomizedOrdersMutation =
        useProcessCustomizedOrders("customizedOrders");

    const handleNavigateBack = useCallback(() => {
        setPage({
            ...page,
            cursor: fetchCustomizedOrders.data?.cursors?.previous ?? "",
            direction: "before",
        });
    }, [fetchCustomizedOrders.data?.cursors?.previous, page]);

    const handleNavigateForward = useCallback(() => {
        setPage({
            ...page,
            cursor: fetchCustomizedOrders.data?.cursors?.next ?? "",
            direction: "after",
        });
    }, [fetchCustomizedOrders.data?.cursors?.next, page]);

    const handleGeneratePdf = useCallback(() => {
        processCustomizedOrdersMutation.mutate(void 0, {
            onSuccess: () => {
                message.showSuccess(
                    `Custom order PDF generation queued successfully, check back sheet in a few mins`,
                );
            },
        });
    }, [message, processCustomizedOrdersMutation]);

    console.log("hello", fetchCustomizedOrders?.data?.records);

    return (
        <Root>
            <TitleContainer>
                <Title>Customized Orders</Title>
                <ButtonContainer>
                    <Button
                        variant="outlined"
                        startIcon={<FilePdf fontSize="small" />}
                        onClick={handleGeneratePdf}>
                        Generate PDFs
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Upload fontSize="small" />}
                        onClick={openFileUpload}>
                        Reimport orders
                    </Button>
                </ButtonContainer>
                <FileUploadModal
                    open={fileUploadOpen}
                    onClose={closeFileUpload}
                />
            </TitleContainer>
            {fetchCustomizedOrders?.data?.records.length === 0 && (
                <EmptyRecords>
                    <EmptyRecordsContainer>
                        <Title>Manage Customized Orders</Title>
                        <div>View status and reimport customized orders</div>
                    </EmptyRecordsContainer>
                </EmptyRecords>
            )}

            {fetchCustomizedOrders.data &&
                (fetchCustomizedOrders.data?.records ?? []).length > 0 && (
                    <>
                        <StyledDataGrid
                            rows={fetchCustomizedOrders?.data?.records ?? []}
                            columns={columns}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 20,
                                    },
                                },
                            }}
                            pageSizeOptions={[10, 20, 30]}
                            disableRowSelectionOnClick={true}
                            components={{
                                Footer: () => (
                                    <DataGridFooter
                                        hasNextRecords={Boolean(
                                            fetchCustomizedOrders.data.cursors
                                                .next,
                                        )}
                                        hasPreviousRecords={Boolean(
                                            fetchCustomizedOrders.data.cursors
                                                .previous,
                                        )}
                                        onNavigateBack={handleNavigateBack}
                                        onNavigateForward={
                                            handleNavigateForward
                                        }
                                    />
                                ),
                            }}
                        />
                    </>
                )}
        </Root>
    );
};
